/* eslint-disable radix */
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import moment from 'moment'
import React, { useCallback, useMemo, useRef } from 'react'
import { BiAccessibility, BiMaleFemale } from 'react-icons/bi'
import { FaRegIdCard } from 'react-icons/fa'
import {
  FiArrowRightCircle,
  FiCalendar,
  FiEdit3,
  FiMail,
  FiMapPin,
  FiSmartphone,
  FiUser,
} from 'react-icons/fi'
import { GiDiamondRing } from 'react-icons/gi'
import { MdSecurity } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import Button from '../../components/Button'
import Header from '../../components/Header'
import Input from '../../components/Input'
import InputSelect from '../../components/InputSelect'
import usePersistedState from '../../hooks/usePersistedState'
import calculaIdade from '../../utils/calculaIdade'
import getValidationErrors from '../../utils/getValidationErrors'
import { UserData } from '../../utils/interfaces'
import { Cpf, RgMask } from '../../utils/masks'
import validaCPF from '../../utils/validaCPF'

type PersonalInfosFormValues = {
  name: string
  email: string
  phone: string
  birthdate: string
  cpf: string
  rg: string
  parental1: string
  parental2: string
  birthPlace: string
  gender: string
  maritalState: string
  deficiency: string
  deficiencyDcr: string
}

const PersonalInfo: React.FC = () => {
  const [userData, setUserData] = usePersistedState<UserData>(
    'userData',
    {} as UserData,
  )
  const navigate = useNavigate()
  const formikRef = useRef<FormikProps<PersonalInfosFormValues>>(null)

  const handleClick = useCallback(() => {
    formikRef.current?.submitForm()
  }, [])

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required('Campo obrigatório.')
          .matches(/\s/g, 'Digite o nome completo')
          .min(3, 'Digite o nome completo'),
        email: Yup.string()
          .required('Campo obrigatório')
          .email('Digite um e-mail vá­lido'),
        phone: Yup.string()
          .max(15, 'Telefone invá­lido')
          .required('Campo obrigatório'),
        birthdate: Yup.string()
          .test(
            '',
            'A data de nascimento não pode ser maior que hoje.',
            (value) =>
              moment() > moment((value ?? '').split('/').reverse().join('-')) ||
              value === '',
          )
          .test(
            '',
            'Data de nascimento invá­lida',
            (value) =>
              moment((value ?? '').split('/').reverse().join('-')).isValid() ||
              value === '',
          )
          .test(
            '',
            'Data de nascimento invá­lida',
            (value) =>
              calculaIdade((value ?? '').split('/').reverse().join('-')) <=
                115 || value === '',
          )
          .required('Campo obrigatório'),
        cpf: Yup.string()
          .required('Campo obrigatório.')
          .test('', 'CPF inválido', (value) => {
            const cpf = value ?? ''
            return (
              validaCPF(cpf.replace(/\./g, '').replace('-', '')) || cpf === ''
            )
          }),
        parental1: Yup.string()
          .required('Campo obrigatório')
          .matches(/\s/g, 'Digite o nome completo')
          .min(3, 'Digite o nome completo'),
        parental2: Yup.string()
          .nullable()
          .matches(/\s/g, 'Digite o nome completo')
          .min(3, 'Digite o nome completo'),
        birthPlace: Yup.string().nullable(),
        gender: Yup.string().required('Campo obrigatório'),
        maritalState: Yup.string().required('Campo obrigatório'),
        deficiency: Yup.string().required('Campo obrigatório'),
        deficiencyDcr: Yup.string().when('deficiency', {
          is: (v: string) =>
            ['sim', 's', 'true', '1'].includes((v ?? '').toLowerCase()),
          then: (schema) => schema.required('Descreva a deficiência'),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [],
  )

  const initialValues: PersonalInfosFormValues = useMemo(
    () => ({
      name: userData.name ?? '',
      email: userData.email ?? '',
      phone: userData.phone ?? '',
      birthdate: userData.birthdate
        ? userData.birthdate.split('-').reverse().join('/')
        : '',
      rg: RgMask(userData?.rg || '') ?? '',
      cpf: Cpf(userData?.cpf || '') ?? '',
      parental1: userData.parental1 ?? '',
      parental2: userData.parental2 ?? '',
      birthPlace: userData.birthPlace ?? '',
      gender: userData.gender ?? '',
      maritalState: userData.maritalState ?? '',
      deficiency: userData.deficiency ?? '',
      deficiencyDcr: userData.deficiencyDcr ?? '',
    }),
    [userData],
  )

  const handleSubmit = useCallback(
    async (
      data: PersonalInfosFormValues,
      helpers: FormikHelpers<PersonalInfosFormValues>,
    ) => {
      try {
        const aa = data.birthdate

        const dia = aa.split('/')[0]
        const mes = aa.split('/')[1]
        const ano = aa.split('/')[2]

        const dataForm = `${ano}-${`0${mes}`.slice(-2)}-${`0${dia}`.slice(-2)}`

        helpers.setErrors({})

        await validationSchema.validate(data, { abortEarly: false })

        setUserData({
          ...userData,
          phone: data.phone,
          email: data.email,
          birthdate: dataForm,
          rg: data.rg?.replace(/\./g, '').replace(/-/g, ''),
          cpf: data.cpf?.replace(/\./g, '').replace(/-/g, ''),
          parental1: data.parental1,
          parental2: data.parental2,
          birthPlace: data.birthPlace,
          gender: data.gender,
          maritalState: data.maritalState,
          deficiency: data.deficiency,
          deficiencyDcr: data.deficiencyDcr,
        })

        navigate('/beneficiary')
      } catch (err: unknown) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((err as any)?.name === 'ValidationError') {
          helpers.setErrors(getValidationErrors(err as Yup.ValidationError))
        }
      }
    },
    [navigate, setUserData, userData, validationSchema],
  )

  return (
    <>
      <Header />
      <Formik
        innerRef={formikRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {() => (
          <Form className="w-full flex flex-col items-center mb-8">
            <div className="w-full mx-auto flex max-w-[600px] flex-col items-center px-[5px] max-[766px]:max-w-[550px]">
              <div className="w-full rounded-[8px] bg-white p-[30px_25px] shadow-[0px_2px_4px_rgba(0,0,0,0.05),0px_6px_12px_rgba(61,69,67,0.05)] mb-[10px] mx-[5px] max-[766px]:p-[20px_15px]">
                <strong className="mb-[15px] flex justify-center text-center text-[18px] text-[#0071ba]">
                  Queremos saber mais sobre você, {userData.name.split(' ')[0]}:
                </strong>
                <Input name="name" placeholder="Nome Completo" icon={FiUser} />
                <Input
                  icon={FiMail}
                  name="email"
                  type="email"
                  placeholder="Seu e-mail pessoal"
                />
                <Input
                  name="phone"
                  mask="phone"
                  prefix="+55 | "
                  placeholder="Seu celular com DDD"
                  icon={FiSmartphone}
                />
                <Input
                  placeholder="CPF"
                  name="cpf"
                  icon={MdSecurity}
                  type="tel"
                  mask="cpf"
                />
                <Input
                  icon={FiCalendar}
                  name="birthdate"
                  mask="date"
                  placeholder="Data de nascimento"
                />
                <Input
                  icon={FaRegIdCard}
                  name="rg"
                  mask="rg"
                  placeholder="RG (apenas números)"
                />
                <Input
                  icon={FiUser}
                  name="parental1"
                  placeholder="Nome da mãe"
                />
                <Input
                  icon={FiUser}
                  name="parental2"
                  placeholder="Nome do pai"
                />
                <Input
                  icon={FiMapPin}
                  name="birthPlace"
                  placeholder="Local de nascimento"
                />

                <InputSelect
                  icon={BiMaleFemale}
                  name="gender"
                  placeholder="Gênero"
                  options={[
                    { label: 'Masculino', value: 'M' },
                    { label: 'Feminino', value: 'F' },
                    { label: 'Outro', value: 'O' },
                  ]}
                />

                <InputSelect
                  icon={GiDiamondRing}
                  name="maritalState"
                  placeholder="Estado Civil"
                  options={[
                    { label: 'Solteiro', value: 'S' },
                    { label: 'Casado', value: 'C' },
                    { label: 'Divorciado', value: 'D' },
                    { label: 'Viúvo', value: 'V' },
                    { label: 'Separado', value: 'P' },
                    { label: 'União Estável', value: 'U' },
                    { label: 'Outro/Desconhecido', value: 'O' },
                  ]}
                />
                <InputSelect
                  icon={BiAccessibility}
                  name="deficiency"
                  placeholder="Possui alguma deficiência?"
                  options={[
                    { label: 'Sim', value: 'S' },
                    { label: 'Não', value: 'N' },
                  ]}
                />
                {formikRef.current?.values.deficiency === 'S' && (
                  <Input
                    icon={FiEdit3}
                    name="deficiencyDcr"
                    placeholder="Descreva a deficiência"
                  />
                )}
              </div>
            </div>
            <Button
              type="submit"
              color="blue"
              onClick={handleClick}
              className="min-w-fit gap-3 px-10"
            >
              Avançar
              <FiArrowRightCircle size={24} />
            </Button>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default PersonalInfo
